import { ReactNode, useEffect } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleLeft } from '@iwoca/icons';
import { Text } from '@iwoca/orion';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';
import { Link, useHistory, useLocation } from 'react-router-dom';

import {
    useGetFundingRequirements,
    useGetLoanDocumentRequirements
} from '../useGetFundingRequirements/useGetFundingRequirements';

type TInnerLayoutProps = {
    title?: ReactNode;
    subheading?: ReactNode;
    children: ReactNode;
    requirementType?: string;
};

const FUNDING_REQUIREMENTS_LIST_PATH = '/funding-requirements/';

export const InnerLayout = ({
    title,
    subheading,
    children,
    requirementType
}: TInnerLayoutProps) => {
    const history = useHistory();
    const { pathname, search } = useLocation();
    const isSubroute = pathname !== FUNDING_REQUIREMENTS_LIST_PATH;

    const { fundingRequirements } = useGetFundingRequirements();
    const { loanDocumentRequirements } = useGetLoanDocumentRequirements();
    const allRequirements = [
        ...fundingRequirements,
        ...loanDocumentRequirements
    ];

    useEffect(() => {
        if (isSubroute && requirementType) {
            tracking.ampli.fundingRequirementOpened({
                requirement: requirementType.toLowerCase(),
                version: '2.0'
            });
        }
    }, []);

    if (isSubroute && requirementType) {
        const requirement = allRequirements.find(
            (req) => req.type === requirementType
        );

        if (
            requirement &&
            requirement.status !== 'REQUIRED' &&
            requirement.status !== 'OPTIONAL'
        ) {
            history.replace(
                maintainStateKey({
                    pathname: FUNDING_REQUIREMENTS_LIST_PATH,
                    search
                })
            );
            return null;
        }
    }

    return (
        <div className="mx-auto my-2xl max-w-[600px] text-primary-10">
            {isSubroute && (
                <Link
                    to={maintainStateKey({
                        pathname: FUNDING_REQUIREMENTS_LIST_PATH,
                        search
                    })}
                    onClick={() =>
                        tracking.ampli.fundingRequirementsBackToListClicked({
                            requirement:
                                requirementType?.toLowerCase() ?? 'unknown',
                            version: '2.0'
                        })
                    }
                    className="mb-xl inline-flex items-center gap-xs text-s font-bold text-primary-40"
                >
                    <IconAngleLeft className="text-l" />
                    <FormattedMessage
                        id="_.funding_requirements.inner_layout.back_to_list"
                        defaultMessage="Back to list"
                    />
                </Link>
            )}
            {title && <Text className="text-3xl font-bold">{title}</Text>}
            {subheading && <Text className="mt-l text-l">{subheading}</Text>}
            <div className="mt-xl">{children}</div>
        </div>
    );
};
